@let hasImage = currentImageId() || currentImageUrl();
@let isFileInfoLoading = isFileMetadataLoading() || (isRemoteFileId() && !imageLoaded);

@if (displayMode() === 'list') {
  <div
    class="w-full flex items-center justify-between gap-3 rounded-lg hover:border-shikenso-purple-base-12 border border-transparent px-3 py-1 transition-all duration-150 hover:shadow-lg"
  >
    <button
      type="button"
      class="flex min-w-0 items-center gap-2 flex-1"
      [class.cursor-pointer]="hasPreview()"
      [class.cursor-default]="!hasPreview()"
      (click)="openListPreview($event)"
    >
      <app-icon [src]="listModeIcon()" alt="file type" class="w-4 h-4 shrink-0" />
      <div class="flex w-full min-w-0 items-center gap-2 text-sm font-medium">
        <div class="flex min-w-0 items-center">
          @if (isFileMetadataLoading()) {
            <ngx-skeleton-loader class="h-4" [theme]="{ width: '96px', height: '16px' }" />
          } @else {
            <span class="truncate">{{ fileName() }}</span>

            @if (fileExtension()) {
              <span class="shrink-0">.{{ fileExtension() }}</span>
            }
          }
        </div>

        @if (isFileMetadataLoading()) {
          <ngx-skeleton-loader class="h-4" [theme]="{ width: '40px', height: '16px' }" />
        } @else if (fileSizeLabel()) {
          <span class="shrink-0 text-xs text-shikenso-purple-base-60">• {{ fileSizeLabel() }}</span>
        }
      </div>
    </button>

    <div class="flex items-center">
      @if (hasPreview()) {
        <button
          mat-icon-button
          type="button"
          class="mat-secondary-transparent-button-color"
          (click)="openListPreview($event)"
        >
          <app-icon src="/icons/zoom-in-area.svg" alt="preview file" [size]="16" />
        </button>
      }

      @if (currentImageId()) {
        <button
          mat-icon-button
          type="button"
          class="mat-secondary-transparent-button-color"
          (click)="onDownloadFile($event)"
        >
          <app-icon src="/icons/download.svg" alt="download file" [size]="16" />
        </button>
      }

      @if (!isViewMode()) {
        <button
          mat-icon-button
          type="button"
          class="mat-secondary-transparent-button-color"
          (click)="onRemoveImage($event)"
        >
          <app-icon src="/icons/trash.svg" alt="remove file" [size]="16" />
        </button>
      }
    </div>
  </div>
} @else {
  <ng-template #uploaderTemplate>
    <div class="relative">
      <div
        class="flex items-center justify-center relative cursor-pointer file-uploader-container"
        data-testid="file-uploader-container"
        role="button"
        [style.width.px]="shape() === 'circle' ? circleSize() : imageWidth()"
        [style.height.px]="shape() === 'circle' ? circleSize() : imageHeight()"
        [class.rounded-lg]="shape() === 'rounded'"
        [class.rounded-full]="shape() === 'circle'"
        [class.cursor-default]="disabled() || isViewMode()"
        [class.pointer-events-none]="disabled()"
        [class.bg-shikenso-gray-base]="!hasImage"
        (click)="onUploaderContainerClick(fileInput, hasImage)"
        (dragenter)="onDragEnter($event)"
        (dragleave)="onDragLeave($event)"
        (dragover)="onDragOver($event)"
        (drop)="onDrop($event)"
      >
        <input
          #fileInput
          type="file"
          data-testid="file-uploader-input"
          class="absolute opacity-0 cursor-pointer pointer-events-none"
          [accept]="fileAccept()"
          [disabled]="disabled() || isViewMode()"
          (change)="onFileSelected($event)"
        />

        <button type="button">
          @if (!hasImage && !isUploading() && imageWidth() > 0 && imageHeight() > 0) {
            <app-icon
              *hasPermission="requiredPermission().length ? requiredPermission() : null"
              src="/icons/upload.svg"
              alt="upload image"
              class="w-6 h-6"
            />
          }

          @if (hasImage && !isUploading() && isCurrentFileImage()) {
            <div
              class="relative"
              [class.flex]="imageError"
              [class.items-center]="imageError"
              [class.justify-center]="imageError"
              [class.bg-shikenso-gray-base]="imageError"
              [style.width.px]="shape() === 'circle' ? circleSize() : imageWidth()"
              [style.height.px]="shape() === 'circle' ? circleSize() : imageHeight()"
              [class.!rounded-lg]="shape() === 'rounded'"
              [class.!rounded-full]="shape() === 'circle'"
            >
              @if (shouldShowSpinner() && !imageLoaded) {
                <div class="absolute inset-0 flex items-center justify-center rounded-full">
                  <ngx-skeleton-loader
                    [style.height.px]="shape() === 'circle' ? circleSize() : imageHeight()"
                    [theme]="{
                      'border-radius': shape() === 'rounded' ? '8px' : '50%',
                      height: (shape() === 'circle' ? circleSize() : imageHeight()) + 'px',
                      width: (shape() === 'circle' ? circleSize() : imageWidth()) + 'px',
                    }"
                  />
                </div>
              }

              <img
                #thumbnailImage
                data-testid="file-uploader-uploaded-image"
                class="uploaded-image rounded-lg w-full h-full"
                alt="uploaded image"
                [ngClass]="{
                  '!object-contain !w-3/4 !h-3/4 !m-auto': imageError && shape() === 'circle',
                  '!object-contain !scale-90 !m-auto': imageError && shape() !== 'circle',
                  'object-cover': shape() === 'circle' && !imageError,
                }"
                [class.!rounded-lg]="shape() === 'rounded'"
                [class.!rounded-full]="shape() === 'circle'"
                [src]="
                  imageError
                    ? placeholderImage
                    : (currentImageUrl() ?? (isRemoteFileId() ? currentImageId() : processedImageUrl()))
                "
                [appImageLoader]="(isRemoteFileId() && !currentImageUrl() ? currentImageId() : '') ?? ''"
                (imageLoading)="imageLoaded = false"
                (imageLoaded)="onImageLoaded()"
                (imageError)="onRemoteImageLoadFailed()"
                (error)="onImageError($event)"
                [class.opacity-50]="disabled()"
                (load)="currentImageUrl() || processedImageUrl() ? (imageLoaded = true) : null"
              />

              <div
                role="button"
                class="overlay"
                [class.!rounded-lg]="shape() === 'rounded'"
                [class.!rounded-full]="shape() === 'circle'"
              >
                <div class="flex items-center gap-1">
                  <app-icon
                    src="/icons/zoom-in-area.svg"
                    alt="search icon"
                    class="invert w-4 h-4"
                    color="var(--shikenso-base-white)"
                  />
                  <span>{{ 'shared.zoom' | translate }}</span>
                </div>
              </div>
            </div>
          }

          @if (hasImage && !isUploading() && !isCurrentFileImage()) {
            <div
              class="flex items-center justify-center bg-shikenso-gray-base"
              [style.width.px]="shape() === 'circle' ? circleSize() : imageWidth()"
              [style.height.px]="shape() === 'circle' ? circleSize() : imageHeight()"
              [class.!rounded-lg]="shape() === 'rounded'"
              [class.!rounded-full]="shape() === 'circle'"
            >
              <app-icon src="/icons/file-description.svg" alt="document" class="w-6 h-6" />
            </div>
          }
        </button>

        @if ((uploadedImage() || currentImageId()) && !isViewMode()) {
          <button
            *hasPermission="requiredPermission()"
            data-testid="file-uploader-remove-button"
            class="w-4 h-4 bg-shikenso-gray-200 rounded-full flex items-center justify-center absolute top-0 right-0 remove-image-button"
            type="button"
            [disabled]="disabled() || isViewMode()"
            [class.cursor-default]="disabled() || isViewMode()"
            [class.pointer-events-none]="disabled() || isViewMode()"
            (click)="onRemoveImage($event)"
          >
            <app-icon src="/icons/x-mark-grey.svg" alt="remove image" class="w-4 h-4" />
          </button>
        }

        @if (isUploading()) {
          <div
            class="absolute flex justify-center items-center w-full h-full bg-shikenso-gray-200"
            [class.rounded-lg]="shape() === 'rounded'"
            [class.rounded-full]="shape() === 'circle'"
          >
            <mat-progress-bar mode="indeterminate" class="!w-1/2 rounded-[50px]" />
          </div>
        }
      </div>

      @if (!isViewMode() && showEditButton()) {
        <div
          class="absolute bottom-0 right-0 w-7 h-7 border z-50 border-shikenso-purple-base rounded-full flex items-center justify-center bg-shikenso-base-white cursor-pointer hover:bg-shikenso-gray-300 active:bg-shikenso-gray-400"
          (click)="onUploadNewClick()"
        >
          <app-icon src="/icons/edit.svg" alt="edit" class="w-5 h-5" />
        </div>
      }

      @if (fileForm() && (fileForm()?.touched || displayError)) {
        <div
          class="custom-form absolute text-[10px] min-w-96 h-5"
          [ngClass]="imageHeight() === 0 || imageWidth() === 0 ? '-bottom-10 left-3' : '-bottom-[13px] left-0'"
        >
          <mat-error [control]="fileForm()" />
        </div>
      }
    </div>
  </ng-template>

  <div class="flex flex-1 items-center gap-4">
    @if (size() === 'big') {
      <div
        class="w-full p-3 bg-white border border-shikenso-purple-base-border rounded-[8px] opacity-100 relative"
        [class.bg-transparent-background-hover]="isUploading()"
        [class.!bg-shikenso-gray-base]="disabled()"
        [class.border-shikenso-pink-base]="(isExclusive() || displayRevertButton()) && !isViewMode()"
        [class.drag-over]="isDragging()"
        [ngClass]="
          fileForm() && !fileForm()?.valid && fileForm()?.errors && (fileForm()?.touched || displayError) && !disabled()
            ? 'border-shikenso-error-base bg-shikenso-error-light'
            : ''
        "
        (dragenter)="onDragEnter($event)"
        (dragleave)="onDragLeave($event)"
        (dragover)="onDragOver($event)"
        (drop)="onDrop($event)"
      >
        <div class="flex items-center h-full gap-0 container">
          <ng-container *ngTemplateOutlet="uploaderTemplate" />
          <div class="ml-3 flex justify-between w-full">
            <div class="flex flex-col justify-center">
              <div
                class="flex gap-1 rounded-full"
                [ngClass]="{ 'hover:bg-shikenso-pink-base-12 px-2 -mx-2 w-fit': !hasPreview() }"
              >
                @if (!isUploading()) {
                  @if (isFileInfoLoading) {
                    <ngx-skeleton-loader
                      class="flex items-center h-[18px]"
                      [theme]="{ width: '96px', height: '18px', margin: '0' }"
                    />
                  } @else {
                    <span
                      class="text-sm"
                      [class.opacity-50]="disabled()"
                      [class.cursor-pointer]="!hasPreview()"
                      (click)="hasPreview() ? null : onUploaderContainerClick(fileInputRef()?.nativeElement, hasImage)"
                    >
                      {{ fileName() }}
                    </span>
                  }

                  @if (description()) {
                    <app-icon
                      src="/icons/info-circle.svg"
                      alt="info-circle"
                      class="w-4 h-4 mt-[2px]"
                      [matTooltip]="description()"
                    />
                  }
                }
              </div>

              <div
                [class.opacity-50]="disabled()"
                class="flex flex-col gap-0.5 mt-0.5 text-xs text-shikenso-purple-base-60"
              >
                @if (!isUploading()) {
                  @if (hasImage && !isFileInfoLoading) {
                    <span>
                      {{ fileSizeLabel() }}
                    </span>
                  } @else if (hasImage) {
                    <ngx-skeleton-loader
                      class="flex items-center h-[14px]"
                      [theme]="{ width: '48px', height: '14px', margin: '0' }"
                    />
                  } @else {
                    <span>{{ 'components.fileUploader.maxSize' | translate: { maxSize: 10 } }}</span>
                  }

                  @if (isFileInfoLoading) {
                    <ngx-skeleton-loader
                      class="flex items-center h-[14px]"
                      [theme]="{ width: '48px', height: '14px', margin: '0' }"
                    />
                  } @else {
                    <span>
                      {{ fileExtension() ? `.${fileExtension()}` : `${allowedFileExtensions().join(', ')}` }}
                    </span>
                  }
                }

                @if (isUploading()) {
                  <div class="flex items-center gap-3">
                    <span class="text-sm">
                      {{
                        'pages.campaignForm.ctaDialog.ctaDetails.deliverables.visualDeliverable.uploading' | translate
                      }}
                    </span>

                    <div class="flex justify-center items-center w-32 h-3 bg-shikenso-gray-200 rounded-full">
                      <mat-progress-bar mode="indeterminate" class="!w-[90%] rounded-[50px]" />
                    </div>
                  </div>
                }
              </div>
            </div>

            <div class="flex items-center justify-center gap-2">
              @if (hasImage && !isViewMode()) {
                <div class="flex items-center justify-center">
                  <button
                    mat-icon-button
                    type="button"
                    class="mat-secondary-transparent-button-color"
                    (click)="onUploadNewClick()"
                  >
                    <app-icon src="/icons/edit.svg" alt="edit" class="w-4 h-4" />
                  </button>
                </div>
              }

              @if (displayRevertButton()) {
                <div class="flex items-center justify-center">
                  <button
                    mat-icon-button
                    type="button"
                    class="mat-secondary-transparent-button-color"
                    [disabled]="disabled()"
                    (click)="reverted.emit()"
                  >
                    <app-icon src="/icons/rotate-360.svg" class="w-4 h-4" />
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="uploaderTemplate" />
    }
  </div>
}

<ng-template #enlargedImageOverlay>
  <div
    class="flex h-full min-h-0 w-full min-w-0 items-center justify-center overflow-auto bg-black/70 p-4 backdrop-blur-[8px]"
    role="presentation"
    (click)="closeEnlargedImage($event)"
  >
    <div
      class="relative mx-auto grid max-h-[90vh] max-w-[90vw] grid-cols-1 grid-rows-1 place-items-center"
      (click)="$event.stopPropagation()"
    >
      @if (!enlargedOverlayImageReady()) {
        <ngx-skeleton-loader
          class="z-0 col-start-1 row-start-1 justify-self-center"
          [theme]="enlargedOverlaySkeletonTheme()"
        />
      }

      <img
        class="uploaded-image z-10 col-start-1 row-start-1 mx-auto block h-auto max-h-[90vh] w-auto max-w-[90vw] justify-self-center object-contain"
        alt="uploaded image"
        [class.rounded-lg]="shape() === 'rounded' && !imageError"
        [ngClass]="{
          '!m-auto !scale-90 !object-contain': imageError && shape() !== 'circle',
          '!object-contain !h-3/4 !w-3/4 !m-auto': imageError && shape() === 'circle',
        }"
        [src]="enlargedOverlayDisplaySrc()"
        [class.opacity-0]="!enlargedOverlayImageReady()"
        [class.opacity-100]="enlargedOverlayImageReady()"
        (click)="$event.stopPropagation()"
        (error)="onEnlargedOverlayImageError($event)"
        (load)="onEnlargedOverlayImageLoaded()"
      />
    </div>
  </div>
</ng-template>

<ng-template #listPreviewOverlay>
  <div
    class="flex h-full w-full items-center justify-center bg-black/70 p-6 backdrop-blur-[8px]"
    role="presentation"
    (click)="closeListPreview($event)"
  >
    <div
      class="relative h-[90vh] w-full max-w-5xl rounded-xl bg-shikenso-base-white p-2"
      (click)="$event.stopPropagation()"
    >
      <div class="absolute -right-8 -top-8 z-10">
        <button mat-icon-button type="button" class="mat-big-icon-button" (click)="closeListPreview($event)">
          <app-icon src="/icons/close.svg" color="var(--shikenso-base-white)" [size]="24" />
        </button>
      </div>

      @if (listPreviewType() === 'image') {
        <img [src]="listPreviewUrl()" alt="preview image" class="h-full w-full rounded-lg object-contain" />
      } @else {
        <iframe [src]="listPreviewSafeUrl()" class="h-full w-full rounded-lg" title="file preview"></iframe>
      }
    </div>
  </div>
</ng-template>
